import { action, computed, observable, makeObservable } from 'mobx'
import { generateUID } from '@voicekey/libraries/generateUID/generateUID'
import { MODALITIES } from 'App.const'
import { PERSON_ID_MAX_LENGTH } from '../projects/person/Person.const'
import dynamicMessages from '../locales/dynamicMessages'
import { TRACKER_API } from '@voicekey/api/tracker/tracker'
import { getURL, URL_TYPES } from '@voicekey/api/http'
import { cropImage } from '@voicekey/libraries/cropImage/cropImage'

const url = getURL(URL_TYPES.TRACKER)

export class PersonBlanksStore {
  constructor (rootStore) {
    makeObservable(this, {
      _personBlanks: observable,
      deletePersonBlank: action,
      personBlanksCorrect: computed,
      addBase64ReaderResultsAsPersonBlanks: action,
      convertFileDataToPersonBlank: action,
      getFacesImages: action,
      deleteAllPersonBlanks: action,
      setPersonBlankMediaSrc: action,
      changePersonBlankId: action,
      createSeveralPersons: action,
    })

    this.rootStore = rootStore
  }

  _personBlanks = null

  get personBlanks () {
    return this._personBlanks
  }

  set personBlanks (value) {
    this._personBlanks = value
  }

  deletePersonBlank (personBlankId) {
    this.personBlanks = this.personBlanks.filter(personBlank => personBlank.internalId !== personBlankId)
  }

  get personBlanksCorrect () {
    return (this.personBlanks || []).filter(personBlank => !personBlank.error)
  }

  createPersonBlank ({ id = generateUID(), photo = null, audio = null, error = null, modality }) {
    return {
      internalId: generateUID(),
      id: id,
      photo: photo,
      audio: audio,
      error: error,
      modality: modality,
      isTemplateLoading: false
    }
  }

  async addBase64ReaderResultsAsPersonBlanks (results) {
    const filesData = Array.isArray(results) ? results : [results]
    let newPersonBlanks = []
    for (const fileData of filesData) {
      newPersonBlanks = [
        ...newPersonBlanks,
        ...await this.convertFileDataToPersonBlank(fileData)
      ]
    }
    this.personBlanks = [
      ...(this.personBlanks || []),
      ...newPersonBlanks
    ]
  }

  async convertFileDataToPersonBlank (fileData) {
    const getFileName = fileData => fileData.file.name.replace(/\.[^/.]+$/, '')
    let error = fileData.error

    const [MIMETypeBase] = fileData.file.type.split('/')
    const modality = Object.values(MODALITIES).find(modality => modality.fileInputData.MIMETypeBase === MIMETypeBase)
    if (modality === MODALITIES.FACE && fileData.base64File) {
      try {
        const base64Faces = await this.getFacesImages(fileData)
        if (base64Faces.length) {
          return base64Faces.map((base64Face, index) => this.createPersonBlank({
            id: getFileName(fileData).slice(0,
              base64Faces.length === 1
                ? PERSON_ID_MAX_LENGTH
                : PERSON_ID_MAX_LENGTH - 2
            ) + (base64Faces.length === 1 ? '' : '_' + (index + 1)),
            modality: modality,
            base64File: base64Face,
            [modality.dataName]: base64Face
          }))
        }
      } catch (e) {
        error = e
      }
    }
    const newPersonBlank = this.createPersonBlank({
      id: fileData.file.name && getFileName(fileData).slice(0, PERSON_ID_MAX_LENGTH),
      modality: modality || MODALITIES.FACE,
      error: error
        ? this.rootStore.messagesStore.extractMessage(error)
        : modality === MODALITIES.FACE ? dynamicMessages.NO_FACES_FOUND : null
    })
    if (modality) {
      newPersonBlank[modality.dataName] = fileData.base64File
    }
    return [newPersonBlank]
  }

  async getFacesImages (fileData) {
    const response = await TRACKER_API.getFacesBorders(url, {
      image_base64: fileData.base64File
    }, this.rootStore.sessionStore.sessionId)
    return await Promise.all(response.data.borders.map(border => {
      return cropImage({
        x: border.point_x_position,
        y: border.point_y_position,
        width: border.face_border_width,
        height: border.face_border_height,
        offsetPercent: this.rootStore.settingsStore.personBorderOffsetPercent
      }, fileData.base64File)
    }))
  }

  deleteAllPersonBlanks () {
    this.personBlanks = []
  }

  setPersonBlankMediaSrc (personBlankId, fileSrc) {
    const personBlank = this.getPersonBlankById(personBlankId)
    if (personBlank) {
      personBlank[personBlank.modality.mediaSrcName] = fileSrc
    }
  }

  getPersonBlankById (personBlankId) {
    return this.personBlanks.find(
      personBlank => personBlank.internalId === personBlankId
    )
  }

  changePersonBlankId (personBlankInternalId, newPersonBlankId) {
    const personBlank = this.getPersonBlankById(personBlankInternalId)
    personBlank.id = newPersonBlankId
    if (personBlank.error && (
      personBlank.error.id === 'FACE_MODEL_ALREADY_EXIST' || personBlank.error.id === 'VOICE_MODEL_ALREADY_EXIST'
    )) {
      personBlank.error = null
    }
  }

  async createSeveralPersons (onAddPerson) {
    const errorPersonBlanks = []
    for (const personBlank of this.personBlanksCorrect) {
      personBlank.isTemplateLoading = true
      const modality = personBlank.modality
      try {
        await this.rootStore.personsStore.createTemplate(modality, personBlank.id, personBlank[modality.dataName], true)
        onAddPerson({
          id: personBlank.id,
          audio: null,
          photo: null,
          [modality.dataName]: personBlank[modality.dataName]
        })
      } catch (e) {
        errorPersonBlanks.push({
          ...personBlank,
          error: this.rootStore.messagesStore.extractMessage(e),
          isTemplateLoading: false
        })
      } finally {
        personBlank.isTemplateLoading = false
      }
    }
    this.rootStore.personsStore.getItems()
    this.personBlanks = this.personBlanks.filter(personBlank => personBlank.error).concat(errorPersonBlanks)
  }
}
